import { Chip, Stack, Typography } from '@mui/joy';
import { ArrowRight } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import {
  hasProductTypeAttributeChanges,
  ProductTypeAttributeChanges,
} from 'companySettings/components/PartFinderProductTypes/VersionHistory';
import { CompareGroup, CompareView } from 'companySettings/components/PartFinderVersionHistory';
import { useComparePartFinderFamilyVersions } from 'companySettings/hooks';
import { IPartFinderFamilyReferenceChange } from 'companySettings/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { Color, Direction, Level, Variant } from 'theme/enums';

interface IReferenceChangeProps {
  label: string;
  change: IPartFinderFamilyReferenceChange;
}

// The product type or category the family moved from, and the one it moved to.
const ReferenceChange = ({ label, change }: IReferenceChangeProps) => (
  <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
    <Typography level={Level.TITLE_SM}>{label}</Typography>
    <Typography level={Level.TITLE_SM} color={Color.DANGER} sx={{ textDecoration: 'line-through' }}>
      {change.from?.name ?? DEFAULT_PLACEHOLDER}
    </Typography>
    <ArrowRight />
    <Typography level={Level.TITLE_SM} color={Color.SUCCESS}>
      {change.to?.name ?? DEFAULT_PLACEHOLDER}
    </Typography>
  </Stack>
);

interface IProps {
  fromVersionId: number;
  toVersionId: number;
  isOpen: boolean;
  onClose: () => void;
}

// What changed between two published versions of a family, older to newer: its product type and
// category, its attributes as a product type's comparison shows them, and its parts.
export const PartFinderFamilyCompare = ({
  fromVersionId,
  toVersionId,
  isOpen,
  onClose,
}: IProps) => {
  const { t } = useTranslation();

  const {
    data: comparison,
    isLoading,
    isError,
  } = useComparePartFinderFamilyVersions(fromVersionId, toVersionId, isOpen);

  const attributes = comparison?.attributes;
  const parts = comparison?.parts;
  const hasAttributeChanges =
    hasProductTypeAttributeChanges(attributes) ||
    Boolean(
      attributes?.used?.length || attributes?.unused?.length || attributes?.regrouped?.length
    );
  const hasPartChanges = Boolean(
    parts?.added?.length || parts?.removed?.length || parts?.valuesChanged
  );

  return (
    <CompareView
      comparison={comparison}
      isLoading={isLoading}
      isError={isError}
      errorMessage={t('errorTitlePartFinderFamilyCompare')}
      isOpen={isOpen}
      onClose={onClose}
    >
      {comparison && (
        <Stack sx={{ gap: 3 }}>
          {(comparison.productType || comparison.category) && (
            <Stack sx={{ gap: 1 }}>
              <Typography level={Level.TITLE_MD}>{t('labelDetails')}</Typography>

              {comparison.productType && (
                <ReferenceChange label={t('labelProductType')} change={comparison.productType} />
              )}

              {comparison.category && (
                <ReferenceChange label={t('labelCategory')} change={comparison.category} />
              )}
            </Stack>
          )}

          <Stack sx={{ gap: 1 }}>
            <Typography level={Level.TITLE_MD}>{t('labelAttributes')}</Typography>

            <ProductTypeAttributeChanges attributes={attributes} />

            {attributes?.used?.length > 0 && (
              <CompareGroup
                title={t('labelAttributesNowUsed', { count: attributes.used.length })}
                color={Color.SUCCESS}
              >
                {attributes.used.map((attribute) => (
                  <Chip key={attribute.name} variant={Variant.OUTLINED}>
                    {attribute.name}
                  </Chip>
                ))}
              </CompareGroup>
            )}

            {attributes?.unused?.length > 0 && (
              <CompareGroup
                title={t('labelAttributesNoLongerUsed', { count: attributes.unused.length })}
                color={Color.DANGER}
              >
                {attributes.unused.map((attribute) => (
                  <Chip key={attribute.name} variant={Variant.OUTLINED}>
                    {attribute.name}
                  </Chip>
                ))}
              </CompareGroup>
            )}

            {attributes?.regrouped?.length > 0 && (
              <CompareGroup
                title={t('labelAttributesRegrouped', { count: attributes.regrouped.length })}
                color={Color.WARNING}
              >
                {attributes.regrouped.map(({ name, from, to }) => (
                  <Stack
                    key={name}
                    direction={Direction.ROW}
                    sx={{ alignItems: 'center', gap: 0.5 }}
                  >
                    <Chip variant={Variant.OUTLINED}>{name}</Chip>
                    <Typography level={Level.BODY_SM}>{from ?? DEFAULT_PLACEHOLDER}</Typography>
                    <ArrowRight />
                    <Typography level={Level.BODY_SM}>{to ?? DEFAULT_PLACEHOLDER}</Typography>
                  </Stack>
                ))}
              </CompareGroup>
            )}

            {!hasAttributeChanges && (
              <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
                {t('labelNoChanges')}
              </Typography>
            )}
          </Stack>

          <Stack sx={{ gap: 1 }}>
            <Typography level={Level.TITLE_MD}>{t('labelParts')}</Typography>

            {parts?.added?.length > 0 && (
              <CompareGroup
                title={t('labelValuesAdded', { count: parts.added.length })}
                color={Color.SUCCESS}
              >
                {parts.added.map((partNumber) => (
                  <Chip key={partNumber} variant={Variant.OUTLINED}>
                    {partNumber}
                  </Chip>
                ))}
              </CompareGroup>
            )}

            {parts?.removed?.length > 0 && (
              <CompareGroup
                title={t('labelValuesRemoved', { count: parts.removed.length })}
                color={Color.DANGER}
              >
                {parts.removed.map((partNumber) => (
                  <Chip key={partNumber} variant={Variant.OUTLINED}>
                    {partNumber}
                  </Chip>
                ))}
              </CompareGroup>
            )}

            {parts?.valuesChanged > 0 && (
              <Typography level={Level.TITLE_SM}>
                {t('labelPartFinderFamilyPartValuesChanged', { count: parts.valuesChanged })}
              </Typography>
            )}

            {!hasPartChanges && (
              <Typography level={Level.TITLE_SM} sx={{ color: 'neutral.600' }}>
                {t('labelNoChanges')}
              </Typography>
            )}
          </Stack>
        </Stack>
      )}
    </CompareView>
  );
};
